import axios from 'axios';
import {deleteCookie, getCookie, setCookie} from '../utils/cookies';
import {env} from "../config/environment";
import {notifyConnectionError} from "./connectionErrorBridge";

export const accessTokenKey = 'accessToken';
export const refreshTokenKey = 'refreshToken';
export const usernameKey = 'username';
export const authRedirectReason = 'authRedirectReason';

axios.defaults.baseURL = env.backendUrl;

interface WhitelistRule {
    pattern: RegExp;
    method?: string;
}

const WHITELIST: WhitelistRule[] = [
    {pattern: /^\/auth\/.*/, method: 'POST'},
    {pattern: /^\/auth\/activate$/, method: 'GET'},
];

axios.interceptors.request.use(
    (config) => {
        const url = config.url ?? '';
        const method = (config.method ?? 'GET').toUpperCase();

        const isWhitelisted = WHITELIST.some(({pattern, method: ruleMethod}) =>
            pattern.test(url) && (!ruleMethod || ruleMethod === method),
        );

        if (!isWhitelisted) {
            const token = getCookie(accessTokenKey);
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        return config;
    },
);

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve();
        }
    });
    failedQueue = [];
};

const refreshAccessToken = async (): Promise<boolean> => {
    const refreshToken = getCookie(refreshTokenKey);

    if (!refreshToken) {
        return false;
    }

    try {
        const response = await axios.post('/auth/refresh', {
            token: refreshToken,
        });

        if (response.status === 200) {
            const {accessToken, refreshToken: newRefreshToken, username} = response.data;
            setCookie(accessTokenKey, accessToken, 30);
            setCookie(refreshTokenKey, newRefreshToken, 30);
            setCookie(usernameKey, username, 30);
            return true;
        }
        return false;
    } catch {
        return false;
    }
};

let sessionExpiredDispatched = false;

axios.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        if (axios.isAxiosError(error) && !error.response && error.code === 'ERR_NETWORK') {
            notifyConnectionError();
        }

        if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({resolve, reject});
                })
                    .then(() => {
                        originalRequest.headers = originalRequest.headers || {};
                        originalRequest.headers.Authorization = `Bearer ${getCookie(accessTokenKey)}`;
                        return axios(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const refreshToken = getCookie(refreshTokenKey);
            const refreshSuccess = refreshToken ? await refreshAccessToken() : false;

            if (refreshSuccess) {
                isRefreshing = false;
                processQueue();

                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${getCookie(accessTokenKey)}`;

                return axios(originalRequest);
            } else {
                isRefreshing = false;
                processQueue(error);

                deleteCookie(accessTokenKey);
                deleteCookie(refreshTokenKey);
                deleteCookie(usernameKey);

                localStorage.setItem(authRedirectReason, 'unauthorized');

                if (!originalRequest.skipSessionExpiredHandling && !sessionExpiredDispatched) {
                    sessionExpiredDispatched = true;
                    window.dispatchEvent(new Event('session-expired'));
                    setTimeout(() => {
                        sessionExpiredDispatched = false;
                    }, 1000);
                }
            }
        }
        return Promise.reject(error);
    }
);

export default axios;
